import { isEmptyObject } from 'jquery';
import React, { useEffect } from 'react';
import { Button, Grid, TextField } from '@material-ui/core';
import { useForm, SubmitHandler, FieldValues, Controller } from 'react-hook-form';

import { useStyles } from './styles';
import { Modal } from 'components/migrated/modal';
import { yupResolver } from '@hookform/resolvers/yup';
import { BankDetailsRegistrationProps } from './types';
import { bankDetailsFormSchema } from '../../validations';
import { useSupplierContext } from 'contexts/complementaryRegistration';
import { useBankInstitutionsGql } from '../../graphql/getBankInstitutions';
import { EnhancedAutocomplete as Autocomplete } from 'components/migrated/enhancedautocomplete';

export const BankDetailsRegistration = ({
  isBankDetailsRegistrationModalOpen,
  personIdLogged,
  onClose,
  editMode,
  bankDetailsData
}: BankDetailsRegistrationProps) => {
  const classes = useStyles();
  const { runBankInstitutionsGql, dataBankInstitutions, loadingBankInstitutions } = useBankInstitutionsGql();
  const {
    control: controlBankDetails,
    handleSubmit: handleSubmitBankDetails,
    reset: resetBankDetails,
    formState: formStateBankDetails
  } = useForm<FieldValues>({
    mode: 'onBlur',
    resolver: yupResolver(bankDetailsFormSchema)
  });
  const { setSupplierData, setHasChanges } = useSupplierContext();

  const { isDirty: isDirtyBankDetails, dirtyFields: dirtyFieldsBankDetails } = formStateBankDetails;

  const handleOnSubmit: SubmitHandler<FieldValues> = async (values) => {
    setSupplierData((prevSupplierData) => {
      const bankDetailsToUpdateIndex = prevSupplierData.banksDetails.findIndex((bankDetailsItem) => {
        if (values.idFDBa) {
          return bankDetailsItem.item.idFDBa === values.idFDBa;
        } else if (bankDetailsData) {
          return (
            bankDetailsItem.item.agency === bankDetailsData.agency &&
            bankDetailsItem.item.bank === bankDetailsData.bank &&
            bankDetailsItem.item.checkingAccount === bankDetailsData.checkingAccount
          );
        } else {
          return false;
        }
      });

      const updatedBankDetailsArray = [...prevSupplierData.banksDetails];

      const objectHasChanges = !isEmptyObject(dirtyFieldsBankDetails);

      if (bankDetailsToUpdateIndex !== -1) {
        updatedBankDetailsArray[bankDetailsToUpdateIndex] = {
          operation: objectHasChanges ? (values.idFDBa ? 'A' : 'I') : '',
          item: {
            ...updatedBankDetailsArray[bankDetailsToUpdateIndex].item,
            agency: values.agency,
            bank: values.bank,
            checkingAccount: values.checkingAccount,
            bankDataNotes: values.bankDataNotes
          }
        };
      } else {
        updatedBankDetailsArray.push({
          operation: 'I',
          item: {
            agency: values.agency,
            bank: values.bank,
            checkingAccount: values.checkingAccount,
            bankDataNotes: values.bankDataNotes
          }
        });
      }

      if (objectHasChanges) setHasChanges(true);

      return {
        ...prevSupplierData,
        personIdOperation: +personIdLogged,
        banksDetails: updatedBankDetailsArray
      };
    });

    onClose();
  };

  useEffect(() => {
    if (editMode === true && bankDetailsData) {
      resetBankDetails(bankDetailsData);
    } else {
      resetBankDetails({});
    }
  }, [isBankDetailsRegistrationModalOpen]);

  useEffect(() => {
    runBankInstitutionsGql();
  }, []);

  return (
    <Modal open={isBankDetailsRegistrationModalOpen} onClose={onClose} title={'DADOS BANCÁRIOS'}>
      <form onSubmit={handleSubmitBankDetails(handleOnSubmit)} className={classes.container}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} md={12} lg={12}>
            <Controller
              name="bank"
              control={controlBankDetails}
              defaultValue=""
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <Autocomplete
                  showKeyProperty
                  data={dataBankInstitutions}
                  loading={loadingBankInstitutions}
                  keyProperty="bankInstitutionId"
                  valueProperty="bankInstitutionName"
                  onChange={onChange}
                  value={value}
                  label="Banco"
                  variant="outlined"
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={4} lg={4}>
            <Controller
              name="agency"
              control={controlBankDetails}
              defaultValue=""
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <TextField
                  value={value}
                  onChange={onChange}
                  fullWidth
                  variant="outlined"
                  placeholder="Agência"
                  label="Agência"
                  error={!!error}
                  helperText={error?.message ?? 'Não informe o dígito.'}
                  inputProps={{ maxLength: 4 }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={8} lg={8}>
            <Controller
              name="checkingAccount"
              control={controlBankDetails}
              defaultValue=""
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <TextField
                  value={value}
                  onChange={(e) => onChange(e.target.value.toUpperCase())}
                  fullWidth
                  variant="outlined"
                  placeholder="Conta Corrente"
                  label="Conta Corrente"
                  error={!!error}
                  helperText={error?.message ?? 'Digite a conta corrente completa com o dígito.'}
                  inputProps={{ maxLength: 15 }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={12}>
            <Controller
              name="bankDataNotes"
              control={controlBankDetails}
              defaultValue=""
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <TextField
                  value={value}
                  onChange={(e) => onChange(e.target.value.toUpperCase())}
                  fullWidth
                  variant="outlined"
                  placeholder="Observações"
                  label="Observações"
                  multiline
                  minRows={3}
                  error={!!error}
                  helperText={error?.message || <span>{`${value.length} / 200`}</span>}
                  inputProps={{ maxLength: 200 }}
                />
              )}
            />
          </Grid>
          <Grid className={classes.buttonsContainer} item xs={12} sm={12} md={12}>
            <Button variant="text" color="primary" onClick={onClose}>
              CANCELAR
            </Button>
            <Button variant="contained" color="primary" type="submit" disabled={editMode && !isDirtyBankDetails}>
              CONFIRMAR
            </Button>
          </Grid>
        </Grid>
      </form>
    </Modal>
  );
};
